import { ChevronRight } from "lucide-react";
import { Link } from "react-router";

import { isDealOpen } from "../../deals/dealStatus";
import { useConfigurationContext } from "../../root/ConfigurationContext";
import type { Deal } from "../../types";
import { formatValue, type FocusRow } from "./ranking";
import { ToneDot } from "./FocusRow";

// Pipelinestripen i høyre kolonne (M4): én stolpe der hvert stadium er så
// bredt som den åpne verdien (minst 4 %), og én linje per stadium med antall
// og verdi. Risiko vises bare som en liten prikk og tekst. Vunnet og Tapt er
// status, ikke stadium, og står aldri her.

const MIN_SHARE = 4;

export const dealValue = (deal: Deal) => {
  const oneTime = deal.one_time_value ?? 0;
  const monthly = deal.monthly_value ?? 0;
  return deal.annual_value ?? (oneTime + 12 * monthly || deal.amount || 0);
};

/** Bredde i prosent per stadium: proporsjonal, minst 4 % for ikke-tomme. */
export const stageWidths = (values: number[]) => {
  const total = values.reduce((sum, v) => sum + v, 0);
  const nonEmpty = values.filter((v) => v > 0).length;
  if (!nonEmpty || !total) return values.map(() => 0);
  const floor = Math.min(MIN_SHARE, 100 / nonEmpty);
  const raw = values.map((v) => (v / total) * 100);
  const small = raw.map((r) => r > 0 && r < floor);
  const rest = 100 - floor * small.filter(Boolean).length;
  const restRaw = raw.reduce((sum, r, i) => sum + (small[i] ? 0 : r), 0);
  return raw.map((r, i) =>
    r <= 0 ? 0 : small[i] ? floor : (r / restRaw) * rest,
  );
};

export const PipelineStrip = ({
  deals,
  rows,
}: {
  deals: Deal[];
  rows: FocusRow[];
}) => {
  const { dealStages } = useConfigurationContext();
  const open = deals.filter((d) => !d.archived_at && isDealOpen(d));
  const stages = dealStages
    .map((stage) => {
      const inStage = open.filter((d) => d.stage === stage.value);
      const risk = rows.filter(
        (r) =>
          r.key.startsWith("deal:") &&
          (r.source as { deal: Deal }).deal.stage === stage.value,
      );
      const noStep = risk.filter((r) =>
        r.signals.some((s) => s.kind === "deal_no_step"),
      ).length;
      const stalled = risk
        .flatMap((r) => r.signals)
        .filter((s) => s.kind === "deal_stalled");
      return {
        stage,
        count: inStage.length,
        value: inStage.reduce((sum, d) => sum + dealValue(d), 0),
        noStep,
        stalled: stalled.length,
        stalledDays: Math.max(0, ...stalled.map((s) => s.days ?? 0)),
      };
    })
    .filter((s) => s.count > 0);
  const total = stages.reduce((sum, s) => sum + s.value, 0);
  const widths = stageWidths(stages.map((s) => s.value));
  const count = open.length;

  return (
    <section
      aria-labelledby="pipeline-strip-title"
      className="rounded-[10px] border bg-card p-5"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 id="pipeline-strip-title" className="text-[13px] font-semibold">
          Pipeline
        </h2>
        <Link
          to="/deals"
          className="flex items-center gap-1 text-[13px] text-muted-foreground tabular-nums hover:text-foreground"
        >
          {count} {count === 1 ? "åpen" : "åpne"} · {formatValue(total)}
          <ChevronRight className="size-3.5" />
        </Link>
      </div>
      {count === 0 ? (
        <p className="mt-3 text-[13px] text-muted-foreground">
          Ingen åpne muligheter.
        </p>
      ) : (
        <>
          <div
            aria-hidden
            className="mt-4 flex h-1.5 w-full gap-0.5 overflow-hidden rounded-full"
          >
            {stages.map((s, i) => (
              <span
                key={s.stage.value}
                className="h-full bg-muted-foreground/40"
                style={{ width: `${widths[i]}%` }}
              />
            ))}
          </div>
          <ul className="mt-4 space-y-3">
            {stages.map((s) => (
              <li key={s.stage.value}>
                <Link
                  to={`/deals?filter=${encodeURIComponent(
                    JSON.stringify({ stage: s.stage.value }),
                  )}`}
                  className="flex items-baseline justify-between gap-2 text-[13px] hover:underline"
                >
                  <span>
                    {s.stage.label}
                    <span className="text-muted-foreground"> · {s.count}</span>
                  </span>
                  <span className="tabular-nums">{formatValue(s.value)}</span>
                </Link>
                {s.noStep > 0 && (
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <ToneDot tone="red" />
                    {s.noStep} uten neste steg
                  </p>
                )}
                {s.stalled > 0 && (
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <ToneDot tone="yellow" />
                    {s.stalled === 1
                      ? `1 stille i ${s.stalledDays} dager`
                      : `${s.stalled} stille, lengst ${s.stalledDays} dager`}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
};
